import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import analytics from '../../../../modules/analytics';
import { flattenServiceGroups } from '../../../../modules/provider/loadServiceGroups';
import { RootState } from '../../../../store/models';
import { MARKETPLACE_BASE_NAME } from '../MarketplaceProfile/constants';

const SERVICE_TRUNCATION_LIMIT = 10;

export const useServiceTruncationHandler = ({ currentProId, hasMarketplaceProfile }) => {
  const profileServices = useSelector<RootState, RootState['profileServiceGroups']>(
    state => state.profileServiceGroups,
  );
  const [showAllServices, setShowAllServices] = useState(false);

  const serviceGroups = useMemo(() => {
    if (!profileServices.loading && profileServices?.[currentProId]?.groups?.length > 0) {
      const allServiceGroups = profileServices[currentProId].groups;
      const serviceGroupsWithServices = allServiceGroups.filter(group => group.services.length > 0);
      return serviceGroupsWithServices;
    }
    return [];
  }, [
    currentProId,
    profileServices,
  ]);

  const services = useMemo(() => flattenServiceGroups(serviceGroups), [serviceGroups]);
  const truncatedServices = services
    .slice(0, SERVICE_TRUNCATION_LIMIT);

  const isShowingAllServices = !hasMarketplaceProfile
  || showAllServices
  || services.length <= 10;

  const serviceGroupsTrimmed = useMemo(() => {
    if (isShowingAllServices) {
      return serviceGroups;
    }

    const servicesByGroup = truncatedServices.reduce((acc, service) => ({
      ...acc,
      [service.servicegroup]: [
        ...(acc?.[service.servicegroup] || []),
        service,
      ],
    }), {});

    const trimmedServiceGroups = serviceGroups
      .map(group => ({
        ...group,
        services: servicesByGroup[group.id] || [],
      }))
      .filter(group => group.services.length > 0);

    return trimmedServiceGroups;
  }, [
    isShowingAllServices,
    serviceGroups,
    truncatedServices,
  ]);

  const handleShowAllServices = () => {
    analytics.track(`${MARKETPLACE_BASE_NAME}_see_all_services_clicked`);
    setShowAllServices(true);
  };

  return {
    isShowingAllServices,
    serviceGroupsTrimmed,
    allServiceGroups: serviceGroups,
    handleShowAllServices,
  };
};
